import { useEffect } from "react";

/**
 * Right slide-in sidebar shell with a sticky 3-step stepper header and a sticky
 * footer (Back / Next / Save). Pure presentation chrome — it owns NO listing
 * state. The parent (MarketPlaceDetails) keeps every handler, all validation,
 * and the byte-identical save payload; this only re-skins WHERE that state is
 * edited. Step bodies are passed in as nodes so the existing editors render
 * unchanged inside each step.
 *
 * Props:
 *   open, onClose
 *   step (1|2|3), setStep
 *   steps          — [{ n, label }]
 *   stepUnlocked   — (n) => boolean : gate jumping to a step
 *   canNext        — boolean : enable the Next button
 *   onSave, saveLabel, saving : footer primary action
 *   step1/step2/step3 : node for each step body
 */
export default function ListingEditorSidebar({
  open,
  onClose,
  step,
  setStep,
  steps = [],
  stepUnlocked = () => true,
  canNext = true,
  onSave,
  saveLabel = "Save",
  saving = false,
  step1,
  step2,
  step3,
  children,
}) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const isLast = step === steps[steps.length - 1]?.n;
  const isFirst = step === steps[0]?.n;
  const body =
    children != null
      ? children
      : step === 1
        ? step1
        : step === 2
          ? step2
          : step3;

  return (
    <div style={S.overlay} onMouseDown={onClose}>
      <aside
        style={S.panel}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Listing editor"
      >
        <header style={S.header}>
          <div style={S.headTop}>
            <h2 style={S.title}>Edit listing</h2>
            <button style={S.close} onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>
          <ol style={S.stepper}>
            {steps.map((s, i) => {
              const active = s.n === step;
              const done = s.n < step;
              const unlocked = stepUnlocked(s.n);
              return (
                <li key={s.n} style={S.stepLi}>
                  <button
                    style={{
                      ...S.stepBtn,
                      cursor: unlocked ? "pointer" : "not-allowed",
                      opacity: unlocked ? 1 : 0.5,
                    }}
                    disabled={!unlocked}
                    onClick={() => unlocked && setStep(s.n)}
                  >
                    <span
                      style={{
                        ...S.stepDot,
                        ...(active ? S.stepDotActive : {}),
                        ...(done ? S.stepDotDone : {}),
                      }}
                    >
                      {done ? "✓" : s.n}
                    </span>
                    <span
                      style={{
                        ...S.stepLabel,
                        color: active ? "#111827" : "#6b7280",
                        fontWeight: active ? 700 : 500,
                      }}
                    >
                      {s.label}
                    </span>
                  </button>
                  {i < steps.length - 1 && <span style={S.stepBar} />}
                </li>
              );
            })}
          </ol>
        </header>

        <div style={S.body}>{body}</div>

        <footer style={S.footer}>
          <div>
            {!isFirst && (
              <button style={S.ghost} onClick={() => setStep(step - 1)}>
                ← Back
              </button>
            )}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button style={S.ghost} onClick={onClose}>
              Close
            </button>
            {!isLast ? (
              <button
                style={{ ...S.primary, opacity: canNext ? 1 : 0.5 }}
                disabled={!canNext}
                onClick={() => canNext && setStep(step + 1)}
              >
                Next →
              </button>
            ) : (
              <button
                style={{ ...S.primary, opacity: saving ? 0.6 : 1 }}
                disabled={saving}
                onClick={onSave}
              >
                {saving ? "Saving…" : saveLabel}
              </button>
            )}
          </div>
        </footer>
      </aside>
    </div>
  );
}

const S = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.45)",
    zIndex: 2000,
    display: "flex",
    justifyContent: "flex-end",
  },
  panel: {
    width: "min(1180px, 97vw)",
    height: "100%",
    background: "#f7f8fc",
    boxShadow: "-12px 0 40px rgba(0,0,0,0.18)",
    display: "flex",
    flexDirection: "column",
    animation: "none",
  },
  header: {
    background: "#fff",
    borderBottom: "1px solid #e6e6ee",
    padding: "14px 22px 0",
    position: "sticky",
    top: 0,
    zIndex: 2,
  },
  headTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  title: { margin: 0, fontSize: 18, fontWeight: 800, color: "#111827" },
  close: {
    border: "none",
    background: "transparent",
    fontSize: 26,
    lineHeight: 1,
    cursor: "pointer",
    color: "#6b7280",
  },
  stepper: {
    display: "flex",
    alignItems: "center",
    listStyle: "none",
    margin: 0,
    padding: "14px 0 12px",
    gap: 4,
  },
  stepLi: { display: "flex", alignItems: "center", gap: 4 },
  stepBtn: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    border: "none",
    background: "transparent",
    padding: "4px 6px",
  },
  stepDot: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    background: "#e5e7eb",
    color: "#6b7280",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 700,
  },
  stepDotActive: { background: "#4f46e5", color: "#fff" },
  stepDotDone: { background: "#c7d2fe", color: "#3730a3" },
  stepLabel: { fontSize: 14, whiteSpace: "nowrap" },
  stepBar: { width: 46, height: 2, background: "#e5e7eb", margin: "0 6px" },
  body: {
    flex: 1,
    overflowY: "auto",
    padding: "18px 22px",
  },
  footer: {
    background: "#fff",
    borderTop: "1px solid #e6e6ee",
    padding: "12px 22px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    position: "sticky",
    bottom: 0,
  },
  ghost: {
    border: "1px solid #d4d7e0",
    background: "#fff",
    borderRadius: 10,
    padding: "9px 16px",
    fontWeight: 600,
    fontSize: 14,
    cursor: "pointer",
    color: "#374151",
  },
  primary: {
    border: "none",
    background: "#4f46e5",
    color: "#fff",
    borderRadius: 10,
    padding: "9px 20px",
    fontWeight: 700,
    fontSize: 14,
    cursor: "pointer",
  },
};
